// ═══════════════════════════════════════════════════════════════
// DONOR GIVING SNAPSHOT REPORT
// Path: src/components/reports/DonorSnapshotReport.jsx
//
// All-donors tabular overview with date range filter and
// giving-by-fund pie chart. Gift count, frequency, recency,
// consecutive years, lifetime giving, average, largest, giving
// level, pathway. Sortable columns, CSV export.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { Camera, Download, Users, DollarSign, ArrowUp, ArrowDown, Filter } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { resolveGivingLevel } from "../../constants/giftTypes";
import { getFiscalDates } from "../../utils/fiscalYear";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

// Map each gift date to the fiscal year it belongs to
const dateToFiscalYear = (dateStr, fiscalStartMonth) => {
  const d = new Date(dateStr + "T12:00:00");
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  return month >= fiscalStartMonth ? year : year - 1;
};

// Count consecutive fiscal years of giving, working backwards
const computeConsecutiveYears = (giftDates, fiscalStartMonth) => {
  if (!giftDates || giftDates.length === 0) return 0;
  const fySet = new Set();
  giftDates.forEach((dateStr) => {
    fySet.add(dateToFiscalYear(dateStr, fiscalStartMonth));
  });
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentFy = currentMonth >= fiscalStartMonth ? now.getFullYear() : now.getFullYear() - 1;
  let startFy = fySet.has(currentFy) ? currentFy : currentFy - 1;
  if (!fySet.has(startFy)) return 0;
  let count = 0;
  while (fySet.has(startFy - count)) count++;
  return count;
};

// Compute frequency label from sorted gift dates
const computeFrequency = (sortedDates) => {
  if (sortedDates.length === 0) return "Never";
  if (sortedDates.length === 1) return "One-time";
  let totalDays = 0;
  for (let i = 1; i < sortedDates.length; i++) {
    const prev = new Date(sortedDates[i - 1] + "T12:00:00");
    const curr = new Date(sortedDates[i] + "T12:00:00");
    totalDays += (curr - prev) / (1000 * 60 * 60 * 24);
  }
  const avgDays = totalDays / (sortedDates.length - 1);
  if (avgDays < 45) return "Monthly";
  if (avgDays < 100) return "Quarterly";
  if (avgDays < 200) return "Semi-Annual";
  if (avgDays < 400) return "Annual";
  return "Irregular";
};

// Pie chart colors
const PIE_COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1",
  "#14b8a6", "#e11d48",
];

// ── Pure SVG Pie Chart ───────────────────────────────────────
const PieChart = ({ slices, size = 200, palette }) => {
  if (!slices || slices.length === 0) return null;
  const total = slices.reduce((s, sl) => s + sl.value, 0);
  if (total === 0) return null;

  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;
  let cumulativeAngle = -Math.PI / 2; // start at top

  const paths = slices.map((sl, i) => {
    const angle = (sl.value / total) * 2 * Math.PI;
    const startX = cx + r * Math.cos(cumulativeAngle);
    const startY = cy + r * Math.sin(cumulativeAngle);
    cumulativeAngle += angle;
    const endX = cx + r * Math.cos(cumulativeAngle);
    const endY = cy + r * Math.sin(cumulativeAngle);
    const largeArc = angle > Math.PI ? 1 : 0;
    const d = `M ${cx} ${cy} L ${startX} ${startY} A ${r} ${r} 0 ${largeArc} 1 ${endX} ${endY} Z`;
    return (
      <path
        key={i}
        d={d}
        fill={sl.color}
        stroke={palette.cardBg}
        strokeWidth={1.5}
      >
        <title>{`${sl.label}: ${fmtCurrency(sl.value)} (${Math.round(sl.value / total * 100)}%)`}</title>
      </path>
    );
  });

  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{paths}</svg>;
};

export const DonorSnapshotReport = () => {
  const {
    palette, members, pathways, chapterSettings, canViewGifts, allGifts, navigate, funds,
  } = useApp();

  const fiscalStartMonth = chapterSettings?.fiscalYearStartMonth || 1;
  const givingLevels = chapterSettings?.giving_levels || [];
  const fiscal = getFiscalDates(fiscalStartMonth);
  const loading = allGifts.length === 0 && members.length === 0;

  // Date range state
  const [dateFrom, setDateFrom] = useState("2000-01-01");
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const setRange = (from, to) => { setDateFrom(from); setDateTo(to); };

  const [sortCol, setSortCol] = useState("lifetimeGiving");
  const [sortDir, setSortDir] = useState("desc");

  // Quick ranges
  const quickRanges = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const monthStr = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastMonthStart = month === 1
      ? `${year - 1}-12-01`
      : `${year}-${String(month - 1).padStart(2, "0")}-01`;
    const lastMonthEnd = new Date(year, month - 1, 0).toISOString().slice(0, 10);

    const prevFyStartYear = parseInt(fiscal.yearStart.slice(0, 4)) - 1;
    const prevFyStart = `${prevFyStartYear}-${fiscal.yearStart.slice(5)}`;
    const prevFyEnd = new Date(new Date(fiscal.yearStart + "T12:00:00").getTime() - 86400000).toISOString().slice(0, 10);
    const prevFyLabel = fiscalStartMonth === 1
      ? `CY ${prevFyStartYear}`
      : `FY ${prevFyStartYear}/${String(prevFyStartYear + 1).slice(2)}`;

    return [
      { label: "All Time", from: "2000-01-01", to: now.toISOString().slice(0, 10) },
      { label: fiscal.label, from: fiscal.yearStart, to: now.toISOString().slice(0, 10) },
      { label: prevFyLabel, from: prevFyStart, to: prevFyEnd },
      { label: "This Month", from: monthStr, to: now.toISOString().slice(0, 10) },
      { label: "Last Month", from: lastMonthStart, to: lastMonthEnd },
      { label: `CY ${year}`, from: `${year}-01-01`, to: now.toISOString().slice(0, 10) },
      { label: `CY ${year - 1}`, from: `${year - 1}-01-01`, to: `${year - 1}-12-31` },
    ];
  }, [fiscal, fiscalStartMonth]);

  // Filter gifts by date range
  const filteredGifts = useMemo(() => {
    if (dateFrom === "2000-01-01" && dateTo >= new Date().toISOString().slice(0, 10)) return allGifts;
    return allGifts.filter((g) => g.gift_date >= dateFrom && g.gift_date <= dateTo);
  }, [allGifts, dateFrom, dateTo]);

  // Giving by fund (for pie chart)
  const fundBreakdown = useMemo(() => {
    if (!canViewGifts) return [];
    const fundMap = {};
    filteredGifts.forEach((g) => {
      const fid = g.fund_id || "__none__";
      if (!fundMap[fid]) fundMap[fid] = 0;
      fundMap[fid] += parseFloat(g.amount) || 0;
    });
    // Resolve fund names
    const slices = Object.entries(fundMap)
      .map(([fid, total]) => {
        const fund = funds.find((f) => f.id === fid);
        return { label: fund?.name || (fid === "__none__" ? "Undesignated" : "Unknown"), value: total };
      })
      .sort((a, b) => b.value - a.value);
    // Assign colors
    return slices.map((s, i) => ({ ...s, color: PIE_COLORS[i % PIE_COLORS.length] }));
  }, [filteredGifts, funds, canViewGifts]);

  // Build per-member aggregates from filtered gifts
  const snapshotRows = useMemo(() => {
    const giftMap = {};
    filteredGifts.forEach((g) => {
      const mid = g.member_id;
      if (!mid) return;
      if (!giftMap[mid]) giftMap[mid] = { dates: [], amounts: [] };
      giftMap[mid].dates.push(g.gift_date);
      giftMap[mid].amounts.push(parseFloat(g.amount) || 0);
    });

    const today = new Date();
    today.setHours(12, 0, 0, 0);

    return members.map((member) => {
      const agg = giftMap[member.id];
      const giftCount = agg ? agg.amounts.length : 0;
      const totalGiven = agg ? agg.amounts.reduce((s, a) => s + a, 0) : 0;
      const avgGift = giftCount > 0 ? totalGiven / giftCount : 0;
      const largestGift = agg ? Math.max(...agg.amounts) : 0;

      const sortedDates = agg ? [...agg.dates].sort() : [];
      const lastGiftDate = sortedDates.length > 0 ? sortedDates[sortedDates.length - 1] : null;
      const recencyDays = lastGiftDate
        ? Math.round((today - new Date(lastGiftDate + "T12:00:00")) / (1000 * 60 * 60 * 24))
        : null;

      const frequency = computeFrequency(sortedDates);
      const consecutiveYears = computeConsecutiveYears(sortedDates, fiscalStartMonth);

      const lifetimeGiving = totalGiven;
      // Manual override wins over the amount-driven tier (regardless
      // of scope) since the snapshot only surfaces a single level.
      const overrideName = (member.givingLevelOverride || "").trim() || null;
      const givingLevel =
        overrideName || resolveGivingLevel(lifetimeGiving, givingLevels);

      const pathwayObj = pathways.find((p) => p.key === member.pathway || p.id === member.pathway);
      const pathwayName = pathwayObj?.name || member.pathway || "—";

      const name = member.donorType === "organization"
        ? member.organization || "Organization"
        : member.donorType === "household"
          ? member.householdName || `${member.firstName || ""} ${member.lastName || ""}`.trim() || "Household"
          : `${member.lastName || ""}, ${member.firstName || ""}`.trim().replace(/^,\s*/, "") || member.credentialedFullName || member.email || "(unnamed)";

      return {
        id: member.cpDonorId || member.id?.slice(0, 8) || "—",
        memberId: member.id,
        name,
        donorType: member.donorType || "individual",
        giftCount,
        frequency,
        recencyDays,
        consecutiveYears,
        lifetimeGiving,
        avgGift,
        largestGift,
        givingLevel: givingLevel || "—",
        pathwayName,
      };
    });
  }, [filteredGifts, members, pathways, fiscalStartMonth, givingLevels]);

  // Sorted rows
  const sortedRows = useMemo(() => {
    const rows = [...snapshotRows];
    rows.sort((a, b) => {
      let aVal = a[sortCol];
      let bVal = b[sortCol];
      if (aVal == null) aVal = sortDir === "asc" ? Infinity : -Infinity;
      if (bVal == null) bVal = sortDir === "asc" ? Infinity : -Infinity;
      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = (bVal || "").toLowerCase();
        return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
    return rows;
  }, [snapshotRows, sortCol, sortDir]);

  // Summary stats
  const stats = useMemo(() => {
    const total = snapshotRows.length;
    const withGifts = snapshotRows.filter((r) => r.giftCount > 0).length;
    const totalGiving = snapshotRows.reduce((s, r) => s + r.lifetimeGiving, 0);
    return { total, withGifts, totalGiving };
  }, [snapshotRows]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir("desc"); }
  };

  // Export CSV
  const handleExport = () => {
    if (sortedRows.length === 0) return;
    const allCols = [
      { key: "id", label: "ID" },
      { key: "name", label: "Name" },
      { key: "donorType", label: "Donor Type" },
    ];
    if (canViewGifts) {
      allCols.push(
        { key: "giftCount", label: "# Gifts" },
        { key: "frequency", label: "Frequency" },
        { key: "recencyDays", label: "Recency (days)" },
        { key: "consecutiveYears", label: "Consecutive Years" },
        { key: "lifetimeGiving", label: "Period Giving" },
        { key: "avgGift", label: "Average Gift" },
        { key: "largestGift", label: "Largest Gift" },
        { key: "givingLevel", label: "Giving Level" },
      );
    }
    allCols.push({ key: "pathwayName", label: "Pathway" });

    const headers = allCols.map((c) => c.label).join(",");
    const rows = sortedRows.map((r) =>
      allCols.map((c) => {
        const v = r[c.key];
        if (v == null) return "";
        if (typeof v === "string") return `"${v.replace(/"/g, '""')}"`;
        if (c.key === "lifetimeGiving" || c.key === "avgGift" || c.key === "largestGift") return (v || 0).toFixed(2);
        return v;
      }).join(",")
    );
    const csv = [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `donor-snapshot-${dateFrom}-to-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Column definitions
  const columns = [
    { key: "id", label: "ID", align: "left", gated: false },
    { key: "name", label: "Name", align: "left", gated: false },
    { key: "donorType", label: "Type", align: "left", gated: false },
    { key: "giftCount", label: "# Gifts", align: "right", gated: true },
    { key: "frequency", label: "Frequency", align: "left", gated: true },
    { key: "recencyDays", label: "Recency", align: "right", gated: true },
    { key: "consecutiveYears", label: "Consec. Yrs", align: "right", gated: true },
    { key: "lifetimeGiving", label: "Giving", align: "right", gated: true },
    { key: "avgGift", label: "Avg Gift", align: "right", gated: true },
    { key: "largestGift", label: "Largest", align: "right", gated: true },
    { key: "givingLevel", label: "Level", align: "left", gated: true },
    { key: "pathwayName", label: "Pathway", align: "left", gated: false },
  ];

  const visibleCols = columns.filter((c) => !c.gated || canViewGifts);

  // Styles
  const thStyleFn = (col) => ({
    padding: "6px 10px",
    textAlign: col.align,
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: sortCol === col.key ? palette.accent1 : palette.text3,
    borderBottom: `2px solid ${palette.cardBorder}`,
    position: "sticky",
    top: 0,
    background: palette.cardBg,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
  });

  const tdStyle = (align) => ({
    padding: "5px 10px",
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    borderBottom: `1px solid ${palette.cardBorder}`,
    textAlign: align,
  });

  const renderCell = (row, col) => {
    const val = row[col.key];
    switch (col.key) {
      case "donorType":
        return (val || "individual").charAt(0).toUpperCase() + (val || "individual").slice(1);
      case "recencyDays":
        return val != null ? `${val}d` : "Never";
      case "lifetimeGiving":
      case "avgGift":
      case "largestGift":
        return val > 0 ? fmtCurrency(val) : "—";
      default:
        return val != null ? val : "—";
    }
  };

  const cellColor = (row, col) => {
    if (col.key === "name") return palette.text1;
    if (col.key === "lifetimeGiving" && row.lifetimeGiving > 0) return palette.accent1;
    return palette.text2;
  };

  const isAllTime = dateFrom === "2000-01-01" && dateTo >= new Date().toISOString().slice(0, 10);

  return (
    <div>
      {/* ── Date Range Filter ─────────────────────────────────── */}
      <Card style={{ marginBottom: 16, padding: 14 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Filter size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
          <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 700, color: palette.text2, fontFamily: FONT_FAMILY }}>
            Date Range:
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{
              padding: "4px 8px", borderRadius: 6, fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`,
              background: palette.inputBg, color: palette.inputColor,
            }}
          />
          <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3 }}>to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{
              padding: "4px 8px", borderRadius: 6, fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`,
              background: palette.inputBg, color: palette.inputColor,
            }}
          />
          <div style={{ width: 1, height: 20, background: palette.cardBorder, margin: "0 4px" }} />
          {quickRanges.map((qr) => (
            <button
              key={qr.label}
              onClick={() => setRange(qr.from, qr.to)}
              style={{
                padding: "4px 10px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 600,
                fontFamily: FONT_FAMILY, cursor: "pointer", border: "none",
                background: dateFrom === qr.from && dateTo === qr.to ? palette.accent1 : `${palette.accent1}10`,
                color: dateFrom === qr.from && dateTo === qr.to ? "#fff" : palette.accent1,
                transition: "all 0.15s",
              }}
            >
              {qr.label}
            </button>
          ))}
        </div>
      </Card>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontFamily: FONT_FAMILY }}>
          Loading donor snapshot...
        </div>
      ) : (
        <>
          {/* ── Summary KPIs ──────────────────────────────────────── */}
          <div style={{ display: "grid", gridTemplateColumns: canViewGifts ? "repeat(3, 1fr)" : "repeat(2, 1fr)", gap: 12, marginBottom: 16 }}>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <Users size={ICON_SIZES.sm} style={{ color: palette.accent1, marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {stats.total.toLocaleString()}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                Total Donors
              </div>
            </Card>
            <Card style={{ textAlign: "center", padding: 14 }}>
              <Users size={ICON_SIZES.sm} style={{ color: "#10b981", marginBottom: 4 }} />
              <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {stats.withGifts.toLocaleString()}
              </div>
              <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                {isAllTime ? "Donors with Gifts" : "Donors in Period"}
              </div>
            </Card>
            {canViewGifts && (
              <Card style={{ textAlign: "center", padding: 14 }}>
                <DollarSign size={ICON_SIZES.sm} style={{ color: "#16a34a", marginBottom: 4 }} />
                <div style={{ fontSize: FONT_SIZES.xl, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                  {fmtCurrency(stats.totalGiving)}
                </div>
                <div style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", color: palette.text3, fontFamily: FONT_FAMILY }}>
                  {isAllTime ? "Total Lifetime Giving" : "Period Giving"}
                </div>
              </Card>
            )}
          </div>

          {/* ── Giving by Fund (Pie Chart) ────────────────────────── */}
          {canViewGifts && fundBreakdown.length > 0 && (
            <Card style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
                fontFamily: FONT_FAMILY, display: "flex", alignItems: "center", gap: 6,
                marginBottom: 16,
              }}>
                <DollarSign size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
                Giving by Fund
                {!isAllTime && (
                  <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 400, color: palette.text3 }}>
                    ({dateFrom} to {dateTo})
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
                <PieChart slices={fundBreakdown} size={ICON_SIZES.xxl} palette={palette} />
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 200 }}>
                  {fundBreakdown.map((sl, i) => {
                    const total = fundBreakdown.reduce((s, x) => s + x.value, 0);
                    const pct = total > 0 ? Math.round(sl.value / total * 100) : 0;
                    return (
                      <div
                        key={i}
                        style={{ display: "flex", alignItems: "center", gap: 8 }}
                      >
                        <span style={{
                          width: 10, height: 10, borderRadius: 2, background: sl.color, flexShrink: 0,
                        }} />
                        <span style={{
                          flex: 1, fontSize: FONT_SIZES.xs, color: palette.text1, fontFamily: FONT_FAMILY,
                          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}>
                          {sl.label}
                        </span>
                        <span style={{
                          fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY,
                          flexShrink: 0,
                        }}>
                          {fmtCurrency(sl.value)}
                        </span>
                        <span style={{
                          fontSize: "0.55rem", fontWeight: 600, color: palette.text3, fontFamily: FONT_FAMILY,
                          flexShrink: 0, width: 30, textAlign: "right",
                        }}>
                          {pct}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          )}

          {/* ── Snapshot Table ─────────────────────────────────────── */}
          <Card>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 12,
            }}>
              <div style={{
                fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
                fontFamily: FONT_FAMILY, display: "flex", alignItems: "center", gap: 6,
              }}>
                <Camera size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
                Donor Giving Snapshot
                <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 400, color: palette.text3 }}>
                  ({sortedRows.length} donors)
                </span>
              </div>
              <ActionButton label="Export CSV" icon={Download} small variant="secondary" onClick={handleExport} />
            </div>

            {sortedRows.length === 0 ? (
              <div style={{ textAlign: "center", padding: 32, color: palette.text3, fontSize: FONT_SIZES.xs }}>
                No donors found.
              </div>
            ) : (
              <div style={{ maxHeight: 600, overflowY: "auto", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
                  <thead>
                    <tr>
                      {visibleCols.map((col) => (
                        <th
                          key={col.key}
                          style={thStyleFn(col)}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                          {sortCol === col.key && (
                            sortDir === "asc"
                              ? <ArrowUp size={ICON_SIZES.xs} style={{ marginLeft: 2, verticalAlign: "middle" }} />
                              : <ArrowDown size={ICON_SIZES.xs} style={{ marginLeft: 2, verticalAlign: "middle" }} />
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row) => (
                      <tr
                        key={row.memberId}
                        style={{ transition: "background 0.15s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = `${palette.accent1}06`; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        {visibleCols.map((col) => (
                          <td
                            key={col.key}
                            style={{
                              ...tdStyle(col.align),
                              fontWeight: col.key === "name" || col.key === "lifetimeGiving" ? 600 : 400,
                              color: col.key === "name" ? palette.accent1 : cellColor(row, col),
                              cursor: col.key === "name" ? "pointer" : "default",
                              whiteSpace: "nowrap",
                            }}
                            onClick={col.key === "name" ? () => {
                              const member = members.find((m) => m.id === row.memberId);
                              if (member) navigate("memberDetail", member);
                            } : undefined}
                          >
                            {renderCell(row, col)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};
